import AccessStateView from "../views/AccessStateView.vue";
// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import InvoiceListView from '../views/InvoiceListView.vue';
import TemplateAdminView from '../views/TemplateAdminView.vue';
import CustomersAdminView from '../views/CustomersAdminView.vue';
import ArticlesAdminView from '../views/ArticlesAdminView.vue';
import CompanyAdminView from '../views/CompanyAdminView.vue';
import UserSettingsView from "../views/UserSettingsView.vue";
import AdminUsersView from "../views/AdminUsersView.vue";
import AdminRolesView from "../views/AdminRolesView.vue";
import AdminGroupsView from "../views/AdminGroupsView.vue";
import AdminIdentityProvidersView from "../views/AdminIdentityProvidersView.vue";
import ReleaseNotesView from "../views/ReleaseNotesView.vue";
import HandbookView from "../views/HandbookView.vue";
import ApiPricingHelpView from "../views/ApiPricingHelpView.vue";
import ProvisioningAdminView from "../views/ProvisioningAdminView.vue";
import ForbiddenView from "../views/ForbiddenView.vue";
import AssignmentPendingView from "../views/AssignmentPendingView.vue";
import LegalPageView from "../views/LegalPageView.vue";
import BankingOposView from "../views/BankingOposView.vue";
import WorkforceView from "../views/WorkforceView.vue";
import BillingAutomationView from "../views/BillingAutomationView.vue";

const PERMISSIONS = {
  ZUGFERD_READ: "zugferd.read",
  USER_PROFILE_READ: "user.profile.read",
  COMPANIES_READ: "companies.read",
  CUSTOMERS_READ: "customers.read",
  ARTICLES_READ: "articles.read",
  FILES_READ: "files.read",
  ADMIN_USERS_READ: "admin.users.read",
  ADMIN_GROUPS_READ: "admin.groups.read",
  ADMIN_ROLES_READ: "admin.roles.read",
  ADMIN_IDENTITY_PROVIDERS_READ: "admin.identityproviders.read",
  PROVISIONING_REQUESTS_READ: "provisioning.requests.read",
  BANKING_READ: "banking.read",
  OPOS_READ: "opos.read",
  WORKFORCE_SELF_READ: "workforce.self.read",
  WORKFORCE_TIME_WRITE: "workforce.time.write",
  WORKFORCE_LEAVE_REQUEST: "workforce.leave.request",
  WORKFORCE_TEAM_READ: "workforce.team.read",
  WORKFORCE_LEAVE_APPROVE: "workforce.leave.approve",
  WORKFORCE_HR_MANAGE: "workforce.hr.manage",
  WORKFORCE_AUDIT_READ: "workforce.audit.read",
  WORKFORCE_IMPORT: "workforce.import",
  BILLING_READ: "billing.read",
};

const MODULES = {
  INVOICING: "invoicing",
  E_INVOICE: "e-invoice",
  KIMAI_INTEGRATION: "kimai-integration",
  BANKING: "banking",
  OPOS: "opos",
  WORKFORCE_TIME: "workforce-time",
  LEAVE: "leave",
};

const routes = [
  {
    path: '/',
    name: 'Invoices',
    component: InvoiceListView,
    meta: {
      permission: PERMISSIONS.ZUGFERD_READ,
      modules: [MODULES.INVOICING, MODULES.E_INVOICE],
    },
  },
  {
    path: "/workforce",
    name: "Workforce",
    component: WorkforceView,
    meta: {
      permissionsAny: [
        PERMISSIONS.WORKFORCE_SELF_READ,
        PERMISSIONS.WORKFORCE_TIME_WRITE,
        PERMISSIONS.WORKFORCE_LEAVE_REQUEST,
        PERMISSIONS.WORKFORCE_TEAM_READ,
        PERMISSIONS.WORKFORCE_LEAVE_APPROVE,
        PERMISSIONS.WORKFORCE_HR_MANAGE,
        PERMISSIONS.WORKFORCE_AUDIT_READ,
        PERMISSIONS.WORKFORCE_IMPORT,
      ],
      modulesAny: [MODULES.WORKFORCE_TIME, MODULES.LEAVE],
      companyFeature: "workforce",
    },
  },
  {
    path: "/settings",
    name: "UserSettings",
    component: UserSettingsView,
    meta: { permission: PERMISSIONS.USER_PROFILE_READ },
  },
  {
    path: "/banking",
    name: "BankingOpos",
    component: BankingOposView,
    meta: {
      capabilitiesAny: [
        { module: MODULES.BANKING, permission: PERMISSIONS.BANKING_READ },
        { module: MODULES.OPOS, permission: PERMISSIONS.OPOS_READ },
      ],
      companyFeature: "banking-opos",
    },
  },
  {
    path: "/billing",
    name: "BillingAutomation",
    component: BillingAutomationView,
    meta: {
      permission: PERMISSIONS.BILLING_READ,
      modules: [MODULES.INVOICING, MODULES.KIMAI_INTEGRATION],
      companyFeature: "billing",
    },
  },
  {
    path: '/admin/company',
    name: 'CompanyAdmin',
    component: CompanyAdminView,
    meta: { permission: PERMISSIONS.COMPANIES_READ, modules: [MODULES.INVOICING] },
  },
  {
    path: '/admin/customers',
    name: 'CustomersAdmin',
    component: CustomersAdminView,
    meta: { permission: PERMISSIONS.CUSTOMERS_READ, modules: [MODULES.INVOICING] },
  },
  {
    path: '/admin/articles',
    name: 'ArticlesAdmin',
    component: ArticlesAdminView,
    meta: { permission: PERMISSIONS.ARTICLES_READ, modules: [MODULES.INVOICING] },
  },
  {
    path: '/admin/templates',
    name: 'TemplateAdmin',
    component: TemplateAdminView,
    meta: { permission: PERMISSIONS.FILES_READ, modules: [MODULES.INVOICING] },
  },
  {
    path: "/admin/users",
    name: "AdminUsers",
    component: AdminUsersView,
    meta: { permission: PERMISSIONS.ADMIN_USERS_READ },
  },
  {
    path: "/admin/groups",
    name: "AdminGroups",
    component: AdminGroupsView,
    meta: { permission: PERMISSIONS.ADMIN_GROUPS_READ },
  },
  {
    path: "/admin/roles",
    name: "AdminRoles",
    component: AdminRolesView,
    meta: { permission: PERMISSIONS.ADMIN_ROLES_READ },
  },
  {
    path: "/admin/identity-providers",
    name: "AdminIdentityProviders",
    component: AdminIdentityProvidersView,
    meta: { permission: PERMISSIONS.ADMIN_IDENTITY_PROVIDERS_READ },
  },
  {
    path: "/admin/provisioning",
    name: "ProvisioningAdmin",
    component: ProvisioningAdminView,
    meta: { permission: PERMISSIONS.PROVISIONING_REQUESTS_READ },
  },
  {
    path: "/release-notes",
    name: "ReleaseNotes",
    component: ReleaseNotesView,
    meta: { permission: PERMISSIONS.USER_PROFILE_READ },
  },
  {
    path: "/help/handbook",
    name: "Handbook",
    component: HandbookView,
    meta: { permission: PERMISSIONS.USER_PROFILE_READ },
  },
  {
    path: "/help/api-pricing",
    name: "ApiPricingHelp",
    component: ApiPricingHelpView,
    meta: { permission: PERMISSIONS.USER_PROFILE_READ },
  },
  {
    path: "/legal/imprint",
    name: "LegalImprint",
    component: LegalPageView,
  },
  {
    path: "/legal/privacy",
    name: "LegalPrivacy",
    component: LegalPageView,
  },
  {
    path: "/legal/terms",
    name: "LegalTerms",
    component: LegalPageView,
  },
  {
    path: "/assignment-pending",
    name: "AssignmentPending",
    component: AssignmentPendingView,
  },
  { path: "/access-suspended", name: "AccessSuspended", component: AccessStateView, props: { kind: "Suspended" } },
  { path: "/access-unavailable", name: "AccessUnavailable", component: AccessStateView, props: { kind: "Unavailable" } },
  { path: "/administrative-mfa", name: "AdministrativeMfa", component: AccessStateView, props: { kind: "Mfa" } },
  {
    path: "/forbidden",
    name: "Forbidden",
    component: ForbiddenView,
  }
  // Additional routes can be added here
];

if (process.env.VUE_APP_DISABLE_AUTH === 'true') {
  const E2EInvoiceRibbonView = require('../views/E2EInvoiceRibbonView.vue').default;
  const E2EInvoiceModalView = require("../views/E2EInvoiceModalView.vue").default;
  const E2EInvoiceFormView = require('../views/E2EInvoiceFormView.vue').default;
  const E2EInvoiceImportModalView = require("../views/E2EInvoiceImportModalView.vue").default;
  const E2ENavbarAdminView = require("../views/E2ENavbarAdminView.vue").default;
  const E2EValidateInvoiceModalView = require("../views/E2EValidateInvoiceModalView.vue").default;
  routes.push({
    path: '/__e2e/invoice-ribbon',
    name: 'E2EInvoiceRibbon',
    component: E2EInvoiceRibbonView
  });
  routes.push({
    path: "/__e2e/invoice-modal",
    name: "E2EInvoiceModal",
    component: E2EInvoiceModalView,
  });
  routes.push({
    path: "/__e2e/invoice-import-modal",
    name: "E2EInvoiceImportModal",
    component: E2EInvoiceImportModalView,
  });
  routes.push({
    path: '/__e2e/invoice-form',
    name: 'E2EInvoiceForm',
    component: E2EInvoiceFormView
  });
  routes.push({
    path: "/__e2e/navbar-admin",
    name: "E2ENavbarAdmin",
    component: E2ENavbarAdminView,
  });
  routes.push({
    path: "/__e2e/validate-modal",
    name: "E2EValidateModal",
    component: E2EValidateInvoiceModalView,
  });
}

const router = createRouter({
  history: createWebHistory(),
  routes
});

export default router;
